import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdatabaseAuto, IdeleteOneResponse, IfilterProps,
  Iuser
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';


export interface IloginAttempt
  extends IdatabaseAuto {

  /** The user who performed the login attempt. */
  user: Iuser;

  /** IP address from which the attempt originated. */
  ip: string;

  /** Whether the attempt succeeded (true) or failed (false). */
  successful: boolean;
}

/**
 * Describes a single login attempt for a user, including the source IP
 * and whether the attempt was successful.
 *
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UserLoginAttempt} class when invoking client operations.
 *
 * @example
 * ```typescript
 * const result = await UserLoginAttempt.getAll();
 *
 * const result = await UserLoginAttempt.filterAll({
 *   // filter criteria
 * });
 *
 * const result = await UserLoginAttempt.bulkDelete({
 *   // delete criteria
 * });
 *
 * const loginAttempt = await UserLoginAttempt.getOne('resourceId');
 *
 * const result = await UserLoginAttempt.remove();
 * ```
 */
export class UserLoginAttempt
  extends DatabaseAuto {
  /** Base path for login attempt routes. */
  private static readonly baseUrl = '/login-attempt';

  /** The user who performed the login attempt. */
  readonly user: IloginAttempt['user'];

  /** IP address from which the attempt originated. */
  readonly ip: IloginAttempt['ip'];

  /** Whether the attempt succeeded (true) or failed (false). */
  readonly successful: IloginAttempt['successful'];


  /**
   * Constructs an instance of {@link UserLoginAttempt}.
   *
   * @param data - The login-attempt data used to initialize the instance.
   */
  constructor(data: IloginAttempt) {
    super(data);

    this.user = data.user;
    this.ip = data.ip;
    this.successful = data.successful;
  }

  /**
   * Retrieves all login attempts with pagination support.
   *
   * @param offset - The starting point for pagination. Defaults to `0`.
   * @param limit - The number of items to retrieve. Defaults to `20`.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserLoginAttempt} instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = AuthClient.ehttp
      .makeGet<IdataArrayResponse<IloginAttempt>>(`${UserLoginAttempt.baseUrl}?${query}`);
    const loginAttempts = await lastValueFrom(observer$);

    return {
      count: loginAttempts.count,
      dataArr: loginAttempts.data.map((val) => new UserLoginAttempt(val))
    };
  }

  /**
   * Filters login attempts based on the provided filter properties.
   *
   * @param filter - The filter criteria for retrieving login attempts.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserLoginAttempt} instances that match the filter.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp
      .makePost<IdataArrayResponse<IloginAttempt>>(`${UserLoginAttempt.baseUrl}/filter`, filter);
    const loginAttempts = await lastValueFrom(observer$);

    return {
      count: loginAttempts.count,
      dataArr: loginAttempts.data.map((val) => new UserLoginAttempt(val))
    };
  }

  /**
   * Retrieves a single login attempt by resource identifier.
   *
   * @param resourceId - The unique identifier for the login attempt to retrieve.
   * @returns A promise that resolves to the {@link UserLoginAttempt} instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AuthClient.ehttp
      .makeGet<IloginAttempt>(`${UserLoginAttempt.baseUrl}/${resourceId}`);
    const loginAttempt = await lastValueFrom(observer$);

    return new UserLoginAttempt(loginAttempt);
  }

  /**
   * Removes multiple login attempts based on the provided criteria.
   *
   * @param payload - The criteria for deleting multiple login attempts.
   * @returns A promise that resolves to the bulk delete operation result.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = AuthClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${UserLoginAttempt.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current login attempt.
   *
   * @returns A promise that resolves to the delete operation result, or `null`.
   */
  remove() {
    const observer$ = AuthClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${UserLoginAttempt.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
